import { GenericDropdownOption } from 'App.types';
import { creatorbase } from '@round/api';
import { SelectProps } from '@round/ui-kit';
import { ValueType } from 'react-select';
import useSelectProps from 'Hooks/useSelectProps';
import { useMemo } from 'react';
import {
    useInfinitePurchaseOrders,
    usePurchaseOrder,
    useTeam,
    useTeamsQueriesByIds,
} from '@round/creatorbase-helpers/api-hooks';
import uniq from 'lodash/uniq';

export type PurchaseOrderOption = GenericDropdownOption<string> &
    creatorbase.PurchaseOrder & { team: creatorbase.Team | null };

export const mapPurchaseOrderToOption = (
    purchaseOrder: creatorbase.PurchaseOrder,
    team: creatorbase.Team | null
): PurchaseOrderOption => ({
    value: purchaseOrder.id,
    label: purchaseOrder.number || purchaseOrder.request_reference || '',
    ...purchaseOrder,
    team,
});

type PropGetter = (
    override?: Pick<SelectProps<PurchaseOrderOption>, 'onChange' | 'isDisabled'>
) => SelectProps<PurchaseOrderOption>;

type Return = { getProps: PropGetter; value: ValueType<PurchaseOrderOption, false>; resetValue: () => void };

type Params = Partial<{
    initialValueId: string | null;
    params: Pick<
        creatorbase.GetPurchaseOrdersParams,
        'currency_id' | 'exclude_allocated_to_campaign_id' | 'exclude_fully_allocated' | 'client_id'
    >;
    enabled: boolean;
}>;

export default function usePurchaseOrderSelect({ initialValueId, params, enabled = true }: Params = {}): Return {
    const { data: purchaseOrder } = usePurchaseOrder(initialValueId, { enabled: !!initialValueId && enabled });
    const { data: team } = useTeam(
        purchaseOrder?.team_id,
        { team_only: false },
        { enabled: !!purchaseOrder?.team_id && enabled }
    );
    const initialValue = useMemo(
        () => (purchaseOrder ? mapPurchaseOrderToOption(purchaseOrder, team ?? null) : null),
        [purchaseOrder, team]
    );

    const { getProps, resetValue, isMenuOpen, debouncedSearchValue } = useSelectProps<PurchaseOrderOption, false>({
        initialValue,
    });
    const query = useInfinitePurchaseOrders(
        { ...params, page_size: 25, search: debouncedSearchValue },
        { enabled: isMenuOpen }
    );

    const teamIds = query.pages.map((page) => uniq(page.results.map((po) => po.team_id))) ?? [];
    const teamsResults = useTeamsQueriesByIds(teamIds);

    const options = useMemo(
        () =>
            query.pages.flatMap((page, pageIndex) => {
                const teamResult = teamsResults[pageIndex];
                return page.results.map((po) =>
                    mapPurchaseOrderToOption(po, teamResult?.data?.results.find((t) => t.id === po.team_id) ?? null)
                );
            }),
        [query.pages, teamsResults]
    );

    return {
        getProps: (override) =>
            getProps({
                ...override,
                options,
                noOptionsMessage: () => 'No purchase orders found',
                isLoading: query.isLoading,
                onMenuScrollToBottom: () => {
                    if (query.hasNextPage && !query.isLoading) {
                        query.fetchNextPage();
                    }
                },
                filterOption: null,
            }),
        value: getProps().value,
        resetValue,
    };
}
